'use client'

import { Crown, Trash2 } from 'lucide-react'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'
import { formatMoney } from '@/lib/money'

interface ClubOfferCardViewProps {
    title: string
    imageUrl?: string | null
    regularMinor: number
    clubMinor: number
    currency: string
    expiryDate?: string | Date | null
    productCount?: number
    onClick?: () => void
    /** When provided, renders a hover delete button on the card. */
    onDelete?: () => void
    /** Render in a compact, non-interactive form (used as the modal live preview). */
    preview?: boolean
    className?: string
}

/**
 * Bold, club-branded offer card. Shared between the grid (real offers) and the
 * modal's live member preview (draft values).
 */
export const ClubOfferCardView = ({
    title,
    imageUrl,
    regularMinor,
    clubMinor,
    currency,
    expiryDate,
    productCount,
    onClick,
    onDelete,
    preview = false,
    className,
}: ClubOfferCardViewProps) => {
    const { t } = useTranslation('club')

    const hasDiscount = clubMinor < regularMinor
    const savingsPct = regularMinor > 0 ? Math.round((1 - clubMinor / regularMinor) * 100) : 0
    const expired = expiryDate ? new Date(expiryDate) < new Date() : false

    const interactive = !!onClick

    return (
        <div
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            onClick={onClick}
            onKeyDown={
                interactive
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              onClick?.()
                          }
                      }
                    : undefined
            }
            className={cn(
                'group relative flex w-full flex-col overflow-hidden rounded-xl border bg-card text-left shadow-sm transition',
                interactive &&
                    'cursor-pointer hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-amber-400',
                className,
            )}
        >
            {onDelete && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation()
                        onDelete()
                    }}
                    aria-label={t('offers.card.delete')}
                    title={t('offers.card.delete')}
                    className="absolute right-2 top-2 z-10 rounded-full bg-black/40 p-1.5 text-white opacity-0 transition hover:bg-red-600 focus:opacity-100 focus:outline-none group-hover:opacity-100"
                >
                    <Trash2 className="h-4 w-4" />
                </button>
            )}

            {/* Club gradient strip */}
            <div className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-yellow-400 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white">
                <Crown className="h-3.5 w-3.5" />
                {t('offers.card.badge')}
            </div>

            {/* Image */}
            <div className={cn('flex items-center justify-center bg-muted', preview ? 'h-28' : 'h-32')}>
                {imageUrl ? (
                    <img src={imageUrl} alt={title} className="h-full w-full object-cover" />
                ) : (
                    <Crown className="h-10 w-10 text-muted-foreground/40" />
                )}
            </div>

            {/* Body */}
            <div className="flex flex-1 flex-col gap-2 p-3">
                <p className="line-clamp-2 font-medium">{title || t('offers.card.untitled')}</p>

                <div className="flex flex-wrap items-center gap-2">
                    {hasDiscount && (
                        <span className="text-muted-foreground line-through">
                            {formatMoney(regularMinor, currency)}
                        </span>
                    )}
                    <span className="text-lg font-semibold">{formatMoney(clubMinor, currency)}</span>
                    {hasDiscount && savingsPct > 0 && (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                            {t('offers.card.savePct', { pct: savingsPct })}
                        </span>
                    )}
                </div>

                <div className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-1 pt-1 text-xs text-muted-foreground">
                    {!preview && (
                        <span className={cn(expired && 'text-red-600')}>
                            {expiryDate
                                ? t('offers.card.expires', { date: format(new Date(expiryDate), 'MMM d, yyyy') })
                                : t('offers.card.noExpiry')}
                        </span>
                    )}
                    {typeof productCount === 'number' && (
                        <span>
                            {!preview && '· '}
                            {t('offers.card.productCount', { count: productCount })}
                        </span>
                    )}
                </div>
            </div>
        </div>
    )
}
